/**
 * =============================================================================
 * PARRAFO CLINICO REDACTADO POR IA
 * =============================================================================
 * BAJO DEMANDA, SIEMPRE. El texto no se pide al abrir la pestana: hace falta
 * que el terapeuta pulse. Dos razones y las dos importan:
 *
 *   - Cada generacion cuesta una llamada a un modelo de pago. Generar al abrir
 *     significa pagar por textos que nadie lee.
 *   - Un texto que aparece solo se lee como si fuera parte del sistema. Uno que
 *     hay que pedir se lee como lo que es: una ayuda de redaccion.
 *
 * LO QUE SE LE DICE AL TERAPEUTA. El aviso de que lo ha redactado una IA sobre
 * datos ya calculados no es letra pequena: va arriba, visible, antes del texto.
 * Quien lo lee tiene que saber que esta leyendo antes de leerlo, no despues.
 *
 * El servidor devuelve un motivo distinto por cada fallo (`reason`) para poder
 * decir algo util en vez de "ha habido un error". Que este apagado no es un
 * error: es un estado, y se ensena como tal.
 * =============================================================================
 */

import React, { useCallback, useState } from "react";
import { Alert, Button, Card, Space, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ReviMotricityApi } from "../../../../api/revi/reviMotricityApi";
import { IconoDestello } from "../../../uiComponents/IconoDestello";
import { useAuth } from "../../../../hooks";

const { Paragraph, Text } = Typography;
const api = new ReviMotricityApi();

/**
 * Motivos que NO son un fallo del sistema y no deben pintarse en rojo.
 *
 * "Esta apagado" y "esta sesion no tiene datos" son respuestas correctas. Un
 * terapeuta que ve un error rojo cada vez que abre una sesion antigua deja de
 * mirar los errores rojos.
 */
const MOTIVOS_INFORMATIVOS = ["apagado", "sin_datos"];

/**
 * Pinta las negritas que marca el modelo con **dobles asteriscos**.
 *
 * POR QUE HACE FALTA. El prompt le pide al modelo que resalte los datos
 * numericos clave, porque un sumario de tres parrafos se lee mucho mejor si las
 * cifras se ven de un vistazo. Pero esta tarjeta pintaba texto plano, asi que
 * lo que salia en pantalla eran los asteriscos literales: "**47 sobre 100**".
 *
 * POR QUE NO SE USA UNA LIBRERIA DE MARKDOWN. Porque no hace falta y porque
 * traeria a esta pantalla la capacidad de renderizar HTML de un texto que
 * escribe un modelo. Aqui se parte por los asteriscos y cada trozo se entrega a
 * React como TEXTO: React escapa todo lo que pinta, asi que aunque el modelo
 * devolviera etiquetas, saldrian como caracteres y no como marcado. Sin
 * `dangerouslySetInnerHTML` no hay superficie que atacar.
 *
 * Lo que no reconoce lo deja tal cual, que es lo correcto: es preferible un
 * asterisco suelto en pantalla a comerse texto clinico.
 */
function conNegritas(texto) {
  return String(texto || "")
    .split(/(\*\*[^*\n]+\*\*)/g)
    .filter((trozo) => trozo !== "")
    .map((trozo, i) => {
      const esNegrita =
        trozo.length > 4 && trozo.startsWith("**") && trozo.endsWith("**");
      const clave = `${i}-${trozo.slice(0, 12)}`;
      return esNegrita ? (
        <strong key={clave}>{trozo.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={clave}>{trozo}</React.Fragment>
      );
    });
}

export function ClinicalSummaryCard({ sessionId }) {
  const { t } = useTranslation("motricity");
  const { accessToken } = useAuth();

  const [texto, setTexto] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aviso, setAviso] = useState(null); // { tipo, mensaje }

  const pedir = useCallback(async () => {
    if (!accessToken || !sessionId) return;
    setCargando(true);
    setAviso(null);
    try {
      const res = await api.getSessionSummary(accessToken, sessionId);
      setTexto(res?.summary || null);
    } catch (e) {
      const motivo = e?.body?.reason;
      const clave = `resumenIa.motivos.${motivo}`;
      const traducido = t(clave);
      setAviso({
        tipo: MOTIVOS_INFORMATIVOS.includes(motivo) ? "info" : "error",
        // Si el motivo no tiene traduccion, se ensena el mensaje del servidor
        // antes que una clave en bruto delante de un terapeuta.
        mensaje:
          traducido !== clave
            ? traducido
            : e?.body?.message || e?.message || t("resumenIa.errorGenerico"),
      });
    } finally {
      setCargando(false);
    }
  }, [accessToken, sessionId, t]);

  if (!sessionId) return null;

  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={
        <Space size={8}>
          <IconoDestello />
          {t("resumenIa.titulo")}
        </Space>
      }
      extra={
        <Button
          type={texto ? "default" : "primary"}
          size="small"
          loading={cargando}
          // El destello solo en la primera generacion. Al regenerar, la accion
          // que importa es "otra vez", no "esto lo hace una IA": eso ya se sabe.
          icon={texto ? <ReloadOutlined /> : <IconoDestello />}
          onClick={pedir}
        >
          {texto ? t("resumenIa.regenerar") : t("resumenIa.generar")}
        </Button>
      }
    >
      {/* El aviso va SIEMPRE, tambien antes de generar nada: quien va a pulsar
          el boton tiene que saber que va a leer. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t("resumenIa.aviso")}
      />

      {aviso ? (
        <Alert
          type={aviso.tipo}
          showIcon
          message={aviso.mensaje}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {texto ? (
        // `pre-line` respeta los saltos: el sumario viene en dos o tres
        // parrafos separados por una linea en blanco y asi se leen como tales.
        <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-line" }}>
          {conNegritas(texto)}
        </Paragraph>
      ) : (
        !aviso && <Text type="secondary">{t("resumenIa.vacio")}</Text>
      )}
    </Card>
  );
}

export default ClinicalSummaryCard;
